<template>
  <IonPage>
    <IonHeader class="ion-no-border px-[10px] bg-white">
      <IonToolbar color="white" class="border-b-2 border-gray-300">
        <IonTitle color="medium"><i class="icon-waves mr-1"></i>Setup</IonTitle>
      </IonToolbar>
    </IonHeader>
    <IonContent class="bg-white">
      <Collapse :when="currentSlide === 'welcome'">
        <img class="rounded-2xl shadow-xl size-40 mx-auto my-6" src="/icon.png" />
      </Collapse>

      <Transition name="fade" mode="out-in">
        <WelcomeSlide v-if="currentSlide === 'welcome'"></WelcomeSlide>
        <BiometricSlide v-else-if="currentSlide === 'biometric'"></BiometricSlide>
        <LocationSlide v-else-if="currentSlide === 'location'"></LocationSlide>
        <BleSlide v-else-if="currentSlide === 'ble'"></BleSlide>
        <CameraSlide v-else-if="currentSlide === 'camera'"></CameraSlide>
        <PhotoGallerySlide v-else-if="currentSlide === 'photos'"></PhotoGallerySlide>
        <FindHelpSlide v-else-if="currentSlide === 'find-help'"></FindHelpSlide>
      </Transition>
    </IonContent>
    <IonFooter color="white" class="ion-no-border p-[10px] pb-[30px]">
      <IonToolbar color="white" class="border-t-2 border-gray-300">
        <PrimaryBlockButton @click="onNext()">{{ buttonText }}</PrimaryBlockButton>
      </IonToolbar>
    </IonFooter>
  </IonPage>
</template>

<script setup lang="ts">
import { useBlePermission } from '@/stores/permissions/ble-permission.store';
import { useCameraPermission } from '@/stores/permissions/camera-permission.store';
import { usePhotoGalleryPermission } from '@/stores/permissions/gallery-permission.store';
import { useLocationPermission } from '@/stores/permissions/location-permission.store';
import { useUser } from '@/stores/user.store';
import {
  createAnimation,
  IonContent,
  IonFooter,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  isPlatform,
  useIonRouter,
} from '@ionic/vue';
import { computed, ref } from 'vue';
import WelcomeSlide from './WelcomeSlide.vue';
import PrimaryBlockButton from '@/components/forms/PrimaryBlockButton.vue';
import LocationSlide from './LocationSlide.vue';
import BleSlide from './BleSlide.vue';
import CameraSlide from './CameraSlide.vue';
import PhotoGallerySlide from './PhotoGallerySlide.vue';
import { Collapse } from 'vue-collapsed';
import { useBiometrics } from '@/stores/biometrics.store';
import BiometricSlide from './BiometricSlide.vue';
import FindHelpSlide from './FindHelpSlide.vue';

const slides = ['welcome', 'biometric', 'location', 'ble', 'camera', 'photos', 'find-help'];
const currentSlide = ref('welcome');
const router = useIonRouter();
const locationPermission = useLocationPermission();
const blePermission = useBlePermission();
const cameraPermission = useCameraPermission();
const photoPermission = usePhotoGalleryPermission();
const biometrics = useBiometrics();
const user = useUser();

const buttonText = computed(() => {
  if (currentSlide.value === 'welcome') {
    return "Let's Begin";
  } else if (currentSlide.value === 'biometric') {
    return biometrics.userEnabled ? 'Next' : 'Open Prompt';
  } else if (currentSlide.value === 'ble') {
    return blePermission.ready ? 'Next' : 'Open Prompt';
  } else if (currentSlide.value === 'location') {
    return locationPermission.ready ? 'Next' : 'Open Prompt';
  } else if (currentSlide.value === 'camera') {
    return cameraPermission.ready ? 'Next' : 'Open Prompt';
  } else if (currentSlide.value === 'photos') {
    return photoPermission.ready ? 'Next' : 'Open Prompt';
  } else if (currentSlide.value === 'find-help') {
    return "Let's Go!";
  }
  return 'Open Prompt';
});

async function onNext() {
  if (currentSlide.value === 'location') {
    await locationPermission.prompt();
    if (isPlatform('android')) {
      await blePermission.initialize();
    }
  } else if (currentSlide.value === 'biometric') {
    await biometrics.prompt();
  } else if (currentSlide.value === 'ble') {
    await blePermission.initialize();
  } else if (currentSlide.value === 'camera') {
    await cameraPermission.prompt();
  } else if (currentSlide.value === 'photos') {
    await photoPermission.prompt();
  } else if (currentSlide.value === 'find-help') {
    user.markPermissionWizardSeen();
    return router.replace('/', () => createAnimation());
  }
  const index = slides.findIndex((s) => s === currentSlide.value);
  currentSlide.value = slides[index + 1];
}
</script>

<style scoped></style>
